import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Avatar, Chip, InputBase, IconButton, Menu, MenuItem, ListItemIcon,
  CircularProgress, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Pagination, Collapse, Tooltip, TextField, FormControl, InputLabel, Select
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import VolumeOffOutlinedIcon from '@mui/icons-material/VolumeOffOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';

import adminAxios from '../../utils/adminAxios';
import { urlImg } from '../../components/common/helpers';

const FILTROS = [
  { key: 'todos', label: 'Todas las personas', icono: <PeopleAltOutlinedIcon fontSize="small" /> },
  { key: 'silenciados', label: 'Con el chat en pausa', icono: <VolumeOffOutlinedIcon fontSize="small" /> },
];
const ALCANCE_TEXTO = { chat: 'Chat con usuarios', soporte: 'Chat de soporte' };
const ORIGEN_TEXTO = { auto: 'automático', admin: 'del equipo' };
const DURACIONES = [
  { horas: 1, texto: '1 hora' },
  { horas: 24, texto: '24 horas' },
  { horas: 168, texto: '7 días' },
  { horas: 720, texto: '30 días' },
];
const MOTIVO_MAX = 200;
const LIMIT = 15;

const fechaHora = (f) => (f ? new Date(f).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

const Metrica = ({ valor, label }) => (
  <Box sx={{ textAlign: 'center', minWidth: 76 }}>
    <Typography sx={{ fontWeight: 700, color: 'primary.main' }}>{valor}</Typography>
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
  </Box>
);

// Panel que se abre bajo una persona: uso del chat, reportes y todo el historial de silencios. Se carga al abrirlo.
const DetalleChat = ({ id, version }) => {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let vivo = true;
    setDatos(null); setError(false);
    adminAxios.get(`/api/admin/personas/${id}/chat`)
      .then((res) => { if (vivo) setDatos(res.data); })
      .catch(() => { if (vivo) setError(true); });
    return () => { vivo = false; };
  }, [id, version]);

  if (error) return <Typography variant="caption" sx={{ color: 'error.oscuro' }}>No se pudo cargar el detalle.</Typography>;
  if (!datos) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} /></Box>;

  const { uso, reportes, silencios } = datos;
  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'sutil' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, color: 'secondary.main' }}>
        <ChatOutlinedIcon fontSize="small" />
        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Uso del chat</Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Metrica valor={uso.mensajes24} label="Mensajes 24 h" />
        <Metrica valor={uso.mensajes7} label="Mensajes 7 días" />
        <Metrica valor={uso.archivos24} label="Archivos 24 h" />
        <Metrica valor={`${uso.mb24} MB`} label="Peso 24 h" />
        <Metrica valor={uso.chatsNuevos24} label="Conversaciones nuevas 24 h" />
        <Metrica valor={uso.soporte24} label="Soporte 24 h" />
        <Metrica valor={uso.soporte7} label="Soporte 7 días" />
        <Metrica valor={`${reportes.recibidos} (${reportes.abiertos} abiertos)`} label="Reportes recibidos" />
        <Metrica valor={reportes.hechos} label="Reportes hechos" />
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: 13, color: 'text.secondary', mb: 0.75 }}>Historial de silencios</Typography>
      {silencios.length === 0 ? (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Nunca ha tenido el chat en pausa.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {silencios.map((s, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', fontSize: 13, color: 'text.secondary' }}>
              <Chip size="small" label={s.vigente ? 'Vigente' : (s.levantadoEn ? 'Levantado' : 'Terminado')}
                sx={{ height: 20, fontWeight: 700, bgcolor: s.vigente ? ((t) => alpha(t.palette.error.main, 0.25)) : 'sutil', color: s.vigente ? 'error.oscuro' : 'text.secondary' }} />
              <span>
                {ALCANCE_TEXTO[s.alcance]} · {ORIGEN_TEXTO[s.origen]} · {fechaHora(s.desde)} → {fechaHora(s.levantadoEn || s.hasta)}
                {s.motivo ? ` · “${s.motivo}”` : ''}
              </span>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const Personas = () => {
  const [params] = useSearchParams();
  const idUrl = params.get('id') || '';

  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ silenciados: 0 });
  const [filtro, setFiltro] = useState('todos');
  const [q, setQ] = useState(params.get('q') || '');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [toast, setToast] = useState(null);
  const [expandido, setExpandido] = useState(idUrl || null);
  const [version, setVersion] = useState(0);        // sube cuando algo cambia, para que el detalle abierto se vuelva a leer

  const [menuAncla, setMenuAncla] = useState(null);
  const [menuPersona, setMenuPersona] = useState(null);
  const [dialogo, setDialogo] = useState(null);     // persona a silenciar
  const [alcance, setAlcance] = useState('chat');
  const [horas, setHoras] = useState(24);
  const [motivo, setMotivo] = useState('');
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await adminAxios.get('/api/admin/personas', {
        params: { q, page, limit: LIMIT, estado: filtro, ...(idUrl ? { id: idUrl } : {}) },
      });
      setItems(res.data.items);
      setTotal(res.data.total);
      setCounts(res.data.counts);
    } catch (e) {
      setToast({ tipo: 'error', msg: 'No se pudo cargar la lista de personas.' });
    } finally {
      setCargando(false);
    }
  }, [q, page, filtro, idUrl]);

  useEffect(() => { setPage(1); }, [q, filtro, idUrl]);
  useEffect(() => {
    const t = setTimeout(cargar, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, q]);

  const abrirMenu = (e, persona) => { setMenuAncla(e.currentTarget); setMenuPersona(persona); };
  const cerrarMenu = () => { setMenuAncla(null); setMenuPersona(null); };

  const abrirSilenciar = (persona) => {
    setDialogo(persona); setAlcance('chat'); setHoras(24); setMotivo(''); setErrores({});
  };

  const silenciar = async () => {
    if (motivo.length > MOTIVO_MAX) { setErrores({ motivo: `Máximo ${MOTIVO_MAX} caracteres` }); return; }
    setGuardando(true);
    try {
      await adminAxios.post(`/api/admin/personas/${dialogo._id}/silenciar`, { alcance, horas, motivo: motivo.trim() });
      setDialogo(null);
      setToast({ tipo: 'success', msg: 'Listo: se pausó el chat y la persona recibió un aviso.' });
      setVersion((v) => v + 1);
      cargar();
    } catch (e) {
      setErrores({ envio: e.response?.data?.msg || 'No se pudo silenciar. Intenta de nuevo.' });
    } finally {
      setGuardando(false);
    }
  };

  const reactivar = async (persona) => {
    try {
      await adminAxios.post(`/api/admin/personas/${persona._id}/reactivar`, { alcance: 'ambos' });
      setToast({ tipo: 'success', msg: 'Chat reactivado: la persona ya puede escribir.' });
      setVersion((v) => v + 1);
      cargar();
    } catch (e) {
      setToast({ tipo: 'error', msg: e.response?.data?.msg || 'No se pudo reactivar el chat.' });
    }
  };

  const totalPaginas = Math.ceil(total / LIMIT);

  return (
    <Box>
      {/* Filtros + búsqueda */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {FILTROS.map((f) => {
            const activo = f.key === filtro;
            return (
              <Box key={f.key} onClick={() => setFiltro(f.key)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1.2, borderRadius: 3, cursor: 'pointer',
                  bgcolor: activo ? 'secondary.main' : 'white', color: activo ? 'primary.main' : 'text.secondary',
                  border: '1px solid', borderColor: activo ? 'secondary.main' : 'sutil', fontWeight: 600, transition: 'all .15s',
                }}>
                {f.icono}
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{f.label}</Typography>
                {f.key === 'silenciados' && (
                  <Chip label={counts.silenciados} size="small"
                    sx={{ height: 20, minWidth: 24, bgcolor: activo ? 'rgba(255,255,255,0.25)' : 'sutil', color: activo ? 'primary.main' : 'text.secondary', fontWeight: 700 }} />
                )}
              </Box>
            );
          })}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'white', border: 1, borderColor: 'sutil', borderRadius: 3, px: 2, py: 1, minWidth: 260 }}>
          <SearchOutlinedIcon sx={{ color: 'text.secondary' }} fontSize="small" />
          <InputBase placeholder="Buscar por nombre o correo…" value={q} onChange={(e) => setQ(e.target.value)} sx={{ fontSize: 14, flex: 1 }} />
        </Box>
      </Box>

      {idUrl && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          Mostrando solo a la persona del aviso.
        </Alert>
      )}

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <Paper sx={{ p: 6, borderRadius: 3, textAlign: 'center', color: 'text.secondary' }}>No hay personas que coincidan.</Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {items.map((p) => {
            const abierto = expandido === p._id;
            const enPausa = p.silencios.length > 0;
            return (
              <Paper key={p._id} sx={{ p: 2.5, borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Tooltip title={abierto ? 'Ocultar detalle' : 'Ver uso e historial'}>
                    <IconButton size="small" onClick={() => setExpandido(abierto ? null : p._id)}
                      sx={{ bgcolor: 'sutil', width: 32, height: 32, transition: 'transform .2s', transform: abierto ? 'rotate(180deg)' : 'none' }}>
                      <ExpandMoreRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Avatar src={p.avatar ? `${urlImg}/users/${p._id}/${p.avatar}` : undefined}
                    sx={{ width: 44, height: 44, bgcolor: (t) => alpha(t.palette.secondary.main, 0.12), color: 'secondary.main' }}>
                    <PersonOutlineOutlinedIcon />
                  </Avatar>

                  <Box sx={{ flexGrow: 1, minWidth: 200 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 700, color: 'primary.main' }}>{p.nombre}</Typography>
                      {p.cuentaNueva && <Chip label="Cuenta nueva" size="small" sx={{ height: 20, fontWeight: 700, bgcolor: (t) => alpha(t.palette.warning.main, 0.3), color: 'warning.oscuro' }} />}
                      {!p.status && <Chip label="Dada de baja" size="small" sx={{ height: 20, fontWeight: 700, bgcolor: 'sutil', color: 'text.secondary' }} />}
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{p.email}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
                      {enPausa ? p.silencios.map((s) => (
                        <Chip key={s.alcance} size="small"
                          label={`${ALCANCE_TEXTO[s.alcance]} en pausa hasta ${fechaHora(s.hasta)} (${ORIGEN_TEXTO[s.origen]})`}
                          sx={{ height: 22, fontWeight: 700, bgcolor: (t) => alpha(t.palette.error.main, 0.25), color: 'error.oscuro' }} />
                      )) : (
                        <Chip size="small" label="Chat activo" sx={{ height: 22, fontWeight: 700, bgcolor: (t) => alpha(t.palette.success.main, 0.2), color: 'success.oscuro' }} />
                      )}
                    </Box>
                  </Box>

                  <Metrica valor={p.uso.mensajes} label="Mensajes hoy" />
                  <Metrica valor={`${p.uso.archivos} · ${p.uso.mb} MB`} label="Archivos hoy" />
                  <Metrica valor={p.uso.soporte} label="Soporte hoy" />

                  <IconButton aria-label={`Acciones de ${p.nombre}`} onClick={(e) => abrirMenu(e, p)}><MoreHorizOutlinedIcon /></IconButton>
                </Box>

                <Collapse in={abierto} timeout="auto" unmountOnExit>
                  <DetalleChat id={p._id} version={version} />
                </Collapse>
              </Paper>
            );
          })}
        </Box>
      )}

      {!cargando && total > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, mt: 4 }}>
          {totalPaginas > 1 && (
            <Pagination count={totalPaginas} page={page} onChange={(e, v) => setPage(v)} color="primary" shape="rounded" size="large"
              sx={{ '& .MuiPaginationItem-root': { fontSize: 16, minWidth: 40, height: 40 } }} />
          )}
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Mostrando {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} de {total}
          </Typography>
        </Box>
      )}

      {/* Acciones por persona */}
      <Menu anchorEl={menuAncla} open={!!menuAncla} onClose={cerrarMenu}>
        <MenuItem onClick={() => { abrirSilenciar(menuPersona); cerrarMenu(); }}>
          <ListItemIcon><VolumeOffOutlinedIcon fontSize="small" /></ListItemIcon>
          Silenciar chat…
        </MenuItem>
        <MenuItem disabled={!menuPersona?.silencios?.length} onClick={() => { reactivar(menuPersona); cerrarMenu(); }}>
          <ListItemIcon><ChatOutlinedIcon fontSize="small" /></ListItemIcon>
          Reactivar chat
        </MenuItem>
        <MenuItem onClick={() => { window.open(`/perfil/${menuPersona._id}`, '_blank'); cerrarMenu(); }}>
          <ListItemIcon><VisibilityOutlinedIcon fontSize="small" /></ListItemIcon>
          Ver perfil
        </MenuItem>
      </Menu>

      {/* Silenciar */}
      <Dialog open={!!dialogo} onClose={() => !guardando && setDialogo(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: 'primary.main' }}>Silenciar a {dialogo?.nombre}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            No podrá mandar mensajes mientras dure la pausa; sus mensajes anteriores se conservan. Recibirá un aviso con el motivo.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="alcance-label">Dónde</InputLabel>
            <Select labelId="alcance-label" label="Dónde" value={alcance} onChange={(e) => setAlcance(e.target.value)}>
              <MenuItem value="chat">Chat con otros usuarios</MenuItem>
              <MenuItem value="soporte">Chat de soporte</MenuItem>
              <MenuItem value="ambos">Los dos</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="duracion-label">Por cuánto tiempo</InputLabel>
            <Select labelId="duracion-label" label="Por cuánto tiempo" value={horas} onChange={(e) => setHoras(e.target.value)}>
              {DURACIONES.map((d) => <MenuItem key={d.horas} value={d.horas}>{d.texto}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            fullWidth multiline minRows={2} size="small" label="Motivo (lo verá la persona)"
            value={motivo} onChange={(e) => setMotivo(e.target.value)}
            inputProps={{ maxLength: MOTIVO_MAX }} helperText={errores.motivo || `${motivo.length}/${MOTIVO_MAX}`} error={!!errores.motivo}
          />
          {errores.envio && <Typography role="alert" variant="body2" sx={{ color: 'error.oscuro', mt: 1.5 }}>{errores.envio}</Typography>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogo(null)} disabled={guardando} sx={{ textTransform: 'none', color: 'text.secondary' }}>Cancelar</Button>
          <Button variant="contained" onClick={silenciar} disabled={guardando} sx={{ textTransform: 'none', borderRadius: 2 }}>
            {guardando ? 'Guardando…' : 'Silenciar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.tipo} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default Personas;
